'use strict';


import {Buildings, isMobile, settingsData, threeJsVariables} from "../../settings";
import {Loader} from "./loader/Loader";
import { Vector3, Mesh, PlaneGeometry, CanvasTexture, MeshStandardMaterial, CylinderGeometry, Raycaster, Box3, Vector2, RepeatWrapping } from "three";
import { degToRad, euclideanModulo } from "three/src/math/MathUtils.js";
import { getCurrentLang, getCurrentLangDirection } from "../components/langSwitcher/langSwitcher";
import { Object3D } from "three";


export class SalesPlane {

	static scope = null;

	constructor() {
		this.cameraAngle = euclideanModulo(-threeJsVariables.perspectiveCamera.rotation.y + degToRad(270), Math.PI * 2);
		this.angle = this.cameraAngle + degToRad(70);
		this.radius = 300;
		this.height = 100;
		this.speed = 0.002;
		this.scale = 6;
		this.angleWings = degToRad(30);
		this.propellerSpeed = 1.2;
		this.planeScene = new Object3D();
		this.bannerHeight = 5;
		this.distance = 0;
		this.messageIndex = 0;
		this.canvas = document.createElement('canvas');
		this.planeMesh = null;
		this.propellerMesh = null;
		this.bannerMesh = null;
		this.bannerSegments = null;
		this.visible = true;
		this.direction = null;
		this.tickPlaneAnimation = this.tickCircleAnimation.bind(this);
		SalesPlane.scope = this;
		this.setHeight();
		this.#setup();
	}

	setHeight() {
		let buildingsArray = Buildings.getBuildingsArray();
        const boundingBox = new Box3();
        buildingsArray.forEach(building => {
            const mesh = building.instances[0];
            mesh.updateMatrixWorld(true);
            const bBox = new Box3().setFromObject(mesh);
            boundingBox.union(bBox);
        });
		const boxSize = new Vector3();
        const size = boundingBox.getSize(boxSize);

		const screenTop = new Vector2(0, 1);
		const raycaster = new Raycaster();
		raycaster.setFromCamera(screenTop, threeJsVariables.perspectiveCamera);
		const rayDir = raycaster.ray.direction.clone().normalize();
		const origin = raycaster.ray.origin.clone();
		let t = 0;
		let step = 1;
		let point = new Vector3().copy(origin);
		while ( Math.pow(point.x, 2) + Math.pow(point.z, 2) < Math.pow(this.radius, 2) ) {
			point.copy(rayDir).multiplyScalar(t).add(origin);
			t += step;
		}
		point.y -= 10;
		(point.y < size.y) ?
			this.height = size.y:
			this.height = (size.y + point.y) / 2;
	}

	#setup() {
		const loader = new Loader();
		const draco = loader.dracoLoader;
		draco.load('/assets/model/plane.glb', (gltf) => {
			this.planeMesh = gltf.scene;
			this.propellerMesh = this.planeMesh.getObjectByName('Cessna_Exterior_Body_MAT_0001');

			this.planeMesh.traverse(el => {
				if (el.isMesh) {
					el.rotateY(degToRad(90));
				}
			});
			this.planeMesh.rotation.x = this.angleWings;

            this.canvas.width = 1840;
            this.canvas.height = 200;

			const texture = new CanvasTexture(this.canvas);
			texture.wrapS = RepeatWrapping;

			const bannerWidth = this.bannerHeight / this.canvas.height * this.canvas.width;
			this.bannerSegments = Math.floor(bannerWidth * 0.5);
            const bannerGeometry = new PlaneGeometry(bannerWidth, this.bannerHeight, this.bannerSegments, 2);

			bannerGeometry.attributes.position.array[3 * ((this.bannerSegments + 1) * 0 + this.bannerSegments) + 0] += this.bannerHeight * 0.5;
			bannerGeometry.attributes.position.array[3 * ((this.bannerSegments + 1) * 2 + this.bannerSegments) + 0] += this.bannerHeight * 0.5;

            const bannerMaterial = new MeshStandardMaterial({ map: texture, side: 2 });
            this.bannerMesh = new Mesh(bannerGeometry, bannerMaterial);

			this.bannerMesh.position.x = bannerWidth * 0.5;
			this.planeScene.add(this.bannerMesh);
			this.planeScene.scale.set(this.scale, this.scale, this.scale);
			this.planeScene.position.y = this.height;
			this.planeScene.visible = false;

			this.#addRopes();
			this.messageIndex = -1;
			this.drawNextMessage();

			// this.canvas.style['position'] = 'absolute';
			// this.canvas.style['z-index'] = '100';
			// this.canvas.style['bottom'] = '0';
			// document.querySelector('#app').append(this.canvas);

			
			this.planeScene.add(this.planeMesh);
			threeJsVariables.scene.add(this.planeScene);

			const interval = setInterval(() => {
				if (!document.querySelector('.mc_id__consolto-anchor')) {
					clearInterval(interval);
					setTimeout(() => {
						this.changeLanguage();
						this.updateVisibility();
						threeJsVariables.composer.addUpdatableElement(this);
					}, 2000);
				}
			}, 500);
		}, undefined, (error) => {
			console.error('An error occurred loading the model:', error);
		});
	}

	tick() {
		this.tickPlaneAnimation();
		this.tickPropellerAnimation();
		this.tickBannerAnimation();
	}
	
	tickCircleAnimation() {
		this.angle -= this.speed;
		const angle = (this.direction === 'rtl') ? this.cameraAngle * 2 - this.angle : this.angle;
		this.planeScene.position.x = this.radius * Math.cos(angle);
		this.planeScene.position.z = this.radius * Math.sin(angle);
		
		this.planeScene.rotation.y = -angle - degToRad(90);
		
		if (this.angle < this.cameraAngle - degToRad(270)) {
			this.angle += Math.PI * 2;
			if (!this.drawNextMessage()) {
				this.tickPlaneAnimation = this.tickFlyAwayAnimation.bind(this);
			}
		}
	}
	
	tickPropellerAnimation() {
		this.propellerMesh.rotation.x += this.propellerSpeed;
	}

	tickFlyAwayAnimation() {
		this.distance += this.radius * this.speed;
		const angle = (this.direction === 'rtl') ? this.cameraAngle * 2 - this.angle : this.angle;
		this.planeScene.position.x = this.radius * Math.cos(angle) + this.distance * Math.sin(this.angle);
		this.planeScene.position.z = this.radius * Math.sin(angle) - this.distance * Math.cos(this.angle);
		this.planeScene.rotation.y = -angle - degToRad(90);
		if (this.planeMesh.rotation.x > degToRad(-20))
			this.planeMesh.rotation.x -= this.speed;
		if (this.planeScene.position.x * this.planeScene.position.x +
			this.planeScene.position.z * this.planeScene.position.z > 1000 * 1000)
			this.dispose();
	}

	tickBannerAnimation() {
		const time = Date.now() * 0.002;
		const vertices = this.bannerMesh.geometry.attributes.position.array;
		for (let i = 0; i < vertices.length; i += 3) {
			const x = vertices[i];
			const y = (1 - Math.floor(i / 3 / (this.bannerSegments + 1))) * this.bannerHeight * 0.5 + 1.71;
			vertices[i + 1] = y;
			if (i % (3 * (this.bannerSegments + 1)) !== 0) {
				vertices[i + 1] += Math.sin(-x / 2 + time) * 0.15;
				vertices[i + 2] = 0.15 + Math.sin(-x / 2 + time) * 0.8;
			}
		}
		this.bannerMesh.geometry.attributes.position.needsUpdate = true;
	}

	drawNextMessage() {
		do {
			this.messageIndex++;
			if (this.messageIndex >= config['crmProjectSettings']['sales'].length) {
				this.messageIndex--;
				return false;
			}
			if (config['crmProjectSettings']['sales'][this.messageIndex].active) {
				break;
			}
		} while (true);
		this.redrawCanvas();
		return true;
	}

	#addRopes() {
		const ropeMaterial = new MeshStandardMaterial({ color: 0xc4af96 });

		function createRope(length, addTo) {
			const radius = 0.04;
			const ropeGeometry = new CylinderGeometry(radius, radius, length, 6);
			const rope = new Mesh(ropeGeometry, ropeMaterial);
			rope.geometry.translate(0, -length / 2, 0);
			addTo.add(rope);
			return rope;
		}

		const firstRopeLength = 5;
		const firstRope = createRope(firstRopeLength, this.planeScene);
		firstRope.rotation.z = Math.PI / 2;
		firstRope.position.set(5.0, 1.71, 0.15);

		const secondRopeLength = 4.5;
		const secondRope = createRope(secondRopeLength, this.planeScene);
		secondRope.rotation.set(0, 0, Math.PI / 2 + Math.asin(this.bannerHeight / 2 / secondRopeLength));
		secondRope.position.set(5.0 + firstRopeLength, 1.71, 0.15);

		const thirdRope = createRope(secondRopeLength, this.planeScene);
		thirdRope.rotation.set(0, 0, Math.PI / 2 + -Math.asin(this.bannerHeight / 2 / secondRopeLength));
		thirdRope.position.set(5.0 + firstRopeLength, 1.71, 0.15);

		this.bannerMesh.position.x += 5 + firstRopeLength + Math.sqrt(secondRopeLength * secondRopeLength - this.bannerHeight * this.bannerHeight / 4);
	}

	redrawCanvas() {
		const ctx = this.canvas.getContext('2d');

		ctx.fillStyle = '#f1e8de';
		ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

		ctx.fillStyle = '#db341a';
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.font = '130px Brush Script MT';
		ctx.fillText(config['crmProjectSettings']['sales'][this.messageIndex].text[getCurrentLang()], this.canvas.width / 2, this.canvas.height / 2);
		this.bannerMesh.material.map.needsUpdate = true;
		this.bannerMesh.material.map.repeat.x = (getCurrentLangDirection() === 'rtl') ? -1 : 1;
	}

	hide() {
		this.visible = false;
		this.updateVisibility();
	}

	show() {
		this.visible = true;
		this.updateVisibility();
	}

	updateVisibility(visible = this.visible) {
		if (this.planeScene)
			this.planeScene.visible = visible;
	}

	changeLanguage() {
		this.direction = getCurrentLangDirection();
		if (!this.direction) return;
		this.planeScene.scale.x = (this.direction === 'rtl') ? -this.scale : this.scale;
		this.redrawCanvas();
	}

	static canActivate() {
		return ( 'sales' in config['crmProjectSettings'] && config['crmProjectSettings']['sales'].length > 0 && !isMobile );
	}
	
	dispose() {
		threeJsVariables.composer.removeUpdatableElement(this);
		this.planeScene.removeFromParent();
		this.planeScene.traverse(el => {
			if (el.isMesh) {
				el.geometry.dispose();
				el.material.dispose();
			}
		});
		
		Promise.resolve()
			.then(() => {
				this.cameraAngle = null;
				this.angle = null;
				this.radius = null;
				this.height = null;
				this.speed = null;
				this.scale = null;
				this.angleWings = null;
				this.propellerSpeed = null;
				this.planeScene = null;
				this.distance = null;
				this.planeMesh = null;
				this.propellerMesh = null;
				this.bannerMesh = null;
				this.bannerHeight = null;
				this.bannerSegments = null;
				this.messageIndex = null;
				this.canvas = null;
				this.visible = true;
				this.direction = null;
				this.tickPlaneAnimation = null;
				SalesPlane.scope = null;
			});
	}
}
